import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import api from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import toast from 'react-hot-toast';
import {
  SparklesIcon,
  LinkIcon,
  CalculatorIcon,
  CubeIcon,
  CircleStackIcon,
  ArrowRightIcon,
  PaperAirplaneIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  LightBulbIcon,
  BoltIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';

// Suggestion chips for quick actions
const SUGGESTION_CHIPS = [
  { label: 'What can I do with my data?', icon: LightBulbIcon },
  { label: 'Find connections between my tables', icon: LinkIcon },
  { label: 'Set up everything automatically', icon: BoltIcon },
  { label: 'Create a revenue metric', icon: CalculatorIcon }
];

export default function DataStudioLanding() {
  const { getCurrentCompanyId, getCurrentCompany, isSuperUser } = useAuthStore();
  const companyId = getCurrentCompanyId();
  const currentCompany = getCurrentCompany();
  const messagesEndRef = useRef(null);
  
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  
  // Check if company is selected (for Super/Managing Admins)
  const noCompanySelected = isSuperUser() && !companyId;
  
  // Fetch overview data
  const { data: overviewData, isLoading: overviewLoading, refetch: refetchOverview } = useQuery({
    queryKey: ['data-studio-overview', companyId],
    queryFn: async () => {
      const response = await api.get('/data-studio/overview', { params: { companyId } });
      return response.data;
    },
    enabled: !!companyId,
    refetchInterval: 30000 // Refresh every 30 seconds
  });
  
  // AI chat mutation
  const chatMutation = useMutation({
    mutationFn: async (userMessage) => {
      const response = await api.post('/data-studio/ai-assist', {
        message: userMessage,
        conversationId,
        companyId
      }, { timeout: 120000 });
      return response.data;
    },
    onSuccess: (data) => {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.response,
        toolCalls: data.toolCalls
      }]);
      setConversationId(data.conversationId);
      setIsTyping(false);
      
      // Refresh overview if tools were called
      if (data.toolCalls?.length > 0) {
        refetchOverview();
      }
    },
    onError: (error) => {
      setIsTyping(false);
      toast.error(error.response?.data?.error || 'Failed to get AI response');
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        isError: true
      }]);
    }
  });
  
  // Auto-setup mutation
  const autoSetupMutation = useMutation({
    mutationFn: async (preview = true) => {
      const response = await api.post('/data-studio/auto-setup', { preview, companyId });
      return response.data;
    },
    onSuccess: (data) => {
      if (data.preview) {
        toast.success(`Found ${data.results.relationships.detected} connections`);
      } else {
        toast.success(data.message);
        refetchOverview();
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Auto-setup failed');
    }
  });
  
  // Scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Handle sending a message
  const handleSendMessage = useCallback((msg) => {
    const messageText = msg || message.trim();
    if (!messageText) return;
    
    setMessages(prev => [...prev, { role: 'user', content: messageText }]);
    setMessage('');
    setIsTyping(true);
    chatMutation.mutate(messageText);
  }, [message, chatMutation]);
  
  // Handle chip click
  const handleChipClick = (chipLabel) => {
    handleSendMessage(chipLabel);
  };
  
  // Handle keyboard submit
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  
  const stats = overviewData?.stats || {};
  const suggestions = overviewData?.suggestions || [];
  
  if (noCompanySelected) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-6 text-center">
          <ExclamationTriangleIcon className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-amber-800 dark:text-amber-200 mb-2">
            No Company Selected
          </h2>
          <p className="text-amber-700 dark:text-amber-300">
            Please select a company from the dropdown in the sidebar to view Data Studio.
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen">
      {/* Hero Header */}
      <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 dark:from-indigo-900 dark:via-purple-900 dark:to-pink-900">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
              <SparklesIcon className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Data Studio</h1>
              <p className="text-white/80">
                Connect, transform, and prepare your data for analysis
              </p>
            </div>
          </div>
          
          {currentCompany && (
            <div className="text-white/60 text-sm">
              Working with: <span className="text-white font-medium">{currentCompany.name || currentCompany}</span>
            </div>
          )}
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-6 -mt-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <StatCard
            label="Data Sources"
            value={stats.dataSources?.ready || 0}
            total={stats.dataSources?.total}
            icon={CircleStackIcon}
            color="blue"
            loading={overviewLoading}
          />
          <StatCard
            label="Connections"
            value={stats.relationships?.active || 0}
            total={stats.relationships?.total}
            icon={LinkIcon}
            color="green"
            loading={overviewLoading}
          />
          <StatCard
            label="Wide Views"
            value={stats.wideViews?.ready || 0}
            total={stats.wideViews?.total}
            icon={CubeIcon}
            color="purple"
            loading={overviewLoading}
          />
          <StatCard
            label="Metrics"
            value={stats.metrics?.active || 0}
            total={stats.metrics?.total}
            icon={CalculatorIcon}
            color="amber"
            loading={overviewLoading}
          />
          <StatCard
            label="Analysis Views"
            value={stats.marts?.ready || 0}
            total={stats.marts?.total}
            icon={CubeIcon}
            color="pink"
            loading={overviewLoading}
          />
        </div>
        
        <div className="grid lg:grid-cols-5 gap-6">
          {/* Left Column - Feature Cards & Suggestions */}
          <div className="lg:col-span-3 space-y-6">
            {/* Quick Suggestions */}
            {suggestions.length > 0 && (
              <div className="space-y-3">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <LightBulbIcon className="w-5 h-5 text-amber-500" />
                  Suggested Next Steps
                </h2>
                <div className="space-y-2">
                  {suggestions.map((suggestion, idx) => (
                    <SuggestionCard key={idx} suggestion={suggestion} />
                  ))}
                </div>
              </div>
            )}
            
            {/* Feature Cards */}
            <div className="space-y-3">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Data Studio Features
              </h2>
              <div className="grid md:grid-cols-3 gap-4">
                <FeatureCard
                  title="Data Connections"
                  description="Connect related tables together so you can analyze data across multiple sources."
                  icon={LinkIcon}
                  href="/data/relationships"
                  color="green"
                  count={stats.wideViews?.ready}
                />
                <FeatureCard
                  title="Business Metrics"
                  description="Define key numbers like Total Revenue or Customer Count for consistent reporting."
                  icon={CalculatorIcon}
                  href="/data/metrics"
                  color="amber"
                  count={stats.metrics?.active}
                />
                <FeatureCard
                  title="Analysis Views"
                  description="Create pre-built data views for specific analyses like Monthly Sales Summary."
                  icon={CubeIcon}
                  href="/data/marts"
                  color="purple"
                  count={stats.marts?.ready}
                />
              </div>
            </div>
            
            {/* Auto Setup Button */}
            {stats.dataSources?.ready >= 2 && stats.relationships?.active === 0 && (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl p-6 border border-blue-200 dark:border-blue-800">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-blue-100 dark:bg-blue-900/50 rounded-lg">
                    <BoltIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                      Quick Setup Available
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                      You have {stats.dataSources?.ready} data sources. Let me automatically find connections 
                      and set up your data for analysis.
                    </p>
                    <button
                      onClick={() => autoSetupMutation.mutate(false)}
                      disabled={autoSetupMutation.isPending}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
                    >
                      {autoSetupMutation.isPending ? (
                        <>
                          <ArrowPathIcon className="w-5 h-5 animate-spin" />
                          Setting up...
                        </>
                      ) : (
                        <>
                          <BoltIcon className="w-5 h-5" />
                          Auto-Setup Now
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Right Column - AI Chat */}
          <div className="lg:col-span-2">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 flex flex-col h-[600px]">
              {/* Chat Header */}
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg">
                  <SparklesIcon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">AI Assistant</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Ask me about your data
                  </p>
                </div>
              </div>
              
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.length === 0 ? (
                  <div className="text-center py-8">
                    <SparklesIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400 mb-6">
                      Hi! I'm your Data Studio assistant. I can help you:
                    </p>
                    <ul className="text-left text-sm text-gray-600 dark:text-gray-400 space-y-2 max-w-xs mx-auto mb-6">
                      <li className="flex items-center gap-2">
                        <CheckCircleIcon className="w-4 h-4 text-green-500" />
                        Find connections between your data
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircleIcon className="w-4 h-4 text-green-500" />
                        Create business metrics
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircleIcon className="w-4 h-4 text-green-500" />
                        Build analysis views
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircleIcon className="w-4 h-4 text-green-500" />
                        Set up everything automatically
                      </li>
                    </ul>
                    
                    {/* Suggestion Chips */}
                    <div className="flex flex-wrap gap-2 justify-center">
                      {SUGGESTION_CHIPS.map((chip, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleChipClick(chip.label)}
                          className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-full text-sm transition-colors flex items-center gap-1.5"
                        >
                          <chip.icon className="w-4 h-4" />
                          {chip.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <>
                    {messages.map((msg, idx) => (
                      <MessageBubble key={idx} message={msg} />
                    ))}
                    {isTyping && (
                      <div className="flex items-center gap-2 text-gray-500">
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                        <span className="text-sm">Thinking...</span>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>
              
              {/* Input */}
              <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask about your data..."
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    disabled={chatMutation.isPending}
                  />
                  <button
                    onClick={() => handleSendMessage()}
                    disabled={!message.trim() || chatMutation.isPending}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white rounded-lg transition-colors flex items-center gap-2"
                  >
                    <PaperAirplaneIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Stat Card Component
function StatCard({ label, value, total, icon: Icon, color, loading }) {
  const colorClasses = {
    blue: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800',
    green: 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 border-green-200 dark:border-green-800',
    purple: 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 border-purple-200 dark:border-purple-800',
    amber: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-800',
    pink: 'bg-pink-50 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400 border-pink-200 dark:border-pink-800'
  };
  
  const iconColorClasses = {
    blue: 'text-blue-500',
    green: 'text-green-500',
    purple: 'text-purple-500',
    amber: 'text-amber-500',
    pink: 'text-pink-500'
  };
  
  return (
    <div className={`p-4 rounded-xl border ${colorClasses[color]} bg-white dark:bg-gray-800 shadow-sm`}>
      <div className="flex items-center gap-3">
        <Icon className={`w-8 h-8 ${iconColorClasses[color]}`} />
        <div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white">
            {loading ? '...' : value}
            {total !== undefined && total !== value && (
              <span className="text-sm font-normal text-gray-400">/{total}</span>
            )}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">{label}</div>
        </div>
      </div>
    </div>
  );
}

// Feature Card Component
function FeatureCard({ title, description, icon: Icon, href, color, count }) {
  const colorClasses = {
    green: 'from-green-500 to-emerald-600',
    amber: 'from-amber-500 to-orange-600',
    purple: 'from-purple-500 to-indigo-600'
  };
  
  return (
    <Link
      to={href}
      className="group bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 hover:shadow-lg transition-all hover:border-transparent hover:ring-2 hover:ring-purple-500/50"
    >
      <div className={`p-3 bg-gradient-to-br ${colorClasses[color]} rounded-lg w-fit mb-4`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
        {title}
        {count > 0 && (
          <span className="ml-2 text-sm font-normal text-gray-400">({count})</span>
        )}
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
        {description}
      </p>
      <div className="flex items-center text-purple-600 dark:text-purple-400 text-sm font-medium group-hover:translate-x-1 transition-transform">
        Open <ChevronRightIcon className="w-4 h-4 ml-1" />
      </div>
    </Link>
  );
}

// Suggestion Card Component
function SuggestionCard({ suggestion }) {
  const priorityClasses = {
    high: 'border-l-red-500 bg-red-50 dark:bg-red-900/20',
    medium: 'border-l-amber-500 bg-amber-50 dark:bg-amber-900/20',
    low: 'border-l-blue-500 bg-blue-50 dark:bg-blue-900/20'
  };
  
  return (
    <div className={`p-4 rounded-lg border-l-4 ${priorityClasses[suggestion.priority] || priorityClasses.low}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h4 className="font-medium text-gray-900 dark:text-white">{suggestion.title}</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400">{suggestion.description}</p>
        </div>
        {suggestion.link && (
          <Link
            to={suggestion.link}
            className="flex-shrink-0 px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-1"
          >
            Go <ArrowRightIcon className="w-4 h-4" />
          </Link>
        )}
      </div>
    </div>
  );
}

// Message Bubble Component
function MessageBubble({ message }) {
  const isUser = message.role === 'user';
  
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[85%] px-4 py-3 rounded-2xl ${
        isUser
          ? 'bg-purple-600 text-white rounded-br-sm'
          : message.isError
            ? 'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 rounded-bl-sm'
            : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-bl-sm'
      }`}>
        {isUser ? (
          <p className="whitespace-pre-wrap">{message.content}</p>
        ) : (
          <div className="prose prose-sm dark:prose-invert max-w-none">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {message.content}
            </ReactMarkdown>
          </div>
        )}
        
        {/* Show tool calls if any */}
        {message.toolCalls?.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Actions performed:</p>
            <div className="space-y-1">
              {message.toolCalls.map((tc, idx) => (
                <div key={idx} className="flex items-center gap-2 text-xs">
                  <CheckCircleIcon className="w-4 h-4 text-green-500" />
                  <span className="text-gray-600 dark:text-gray-400">
                    {tc.name.replace(/_/g, ' ')}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

